import React from 'react';
import { Box, CircularProgress, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { translate as t } from 'hooks/useTranslations';
import { neutral, info } from '_lex/_foundations/phxColors';
import { useRiskPoolOrgContext } from '../../RiskPoolOrgContext';
import usePolicyMetrics from '../_data_sources/usePolicyMetrics';
import { SingleOrgMetricsResponse, MetricStatus } from '../_types';
import { STATUS_COLORS } from '../_constants';

interface MetricCardProps {
  title: string;
  tooltip: string;
  value: string;
  badge: string;
  status?: MetricStatus;
  noData?: boolean;
}

function MetricCard({ title, tooltip, value, badge, status = 'GOOD', noData = false }: MetricCardProps) {
  return (
    <Tooltip title={tooltip} arrow placement='top'>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          backgroundColor: 'white',
          border: `1px solid ${noData ? neutral[200] : STATUS_COLORS[status]}`,
          borderRadius: 2,
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant='subtitleMd' sx={{ color: neutral[500] }}>
            {title}
          </Typography>
          <InfoOutlinedIcon sx={{ fontSize: 16, color: info[800], flexShrink: 0 }} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
          <Typography sx={{ fontSize: 32, fontWeight: 500, lineHeight: 1.15, color: neutral[800] }}>{value}</Typography>
          <Typography sx={{ fontSize: 14, color: neutral[500], whiteSpace: 'nowrap' }}>
            {noData ? t('dashboard.riskPool.riskPoolOrg.metrics.noData') : badge}
          </Typography>
        </Box>
      </Box>
    </Tooltip>
  );
}

const getCardsConfig = (data: SingleOrgMetricsResponse) => {
  const pctDisplay = (decimal: number) => `${Math.round(decimal)}%`;

  return [
    {
      key: 'completedPolicyAcks',
      title: t('dashboard.riskPool.riskPoolOrg.metrics.completedPolicyAcks.title'),
      tooltip: t('dashboard.riskPool.riskPoolOrg.metrics.completedPolicyAcks.tooltip'),
      value: pctDisplay(data.completedPolicyAcks.percent),
      badge: t('dashboard.riskPool.riskPoolOrg.metrics.completedPolicyAcks.target'),
      status: data.completedPolicyAcks.status,
    },
    {
      key: 'completedDtbs',
      title: t('dashboard.riskPool.riskPoolOrg.metrics.completedDtbs.title'),
      tooltip: t('dashboard.riskPool.riskPoolOrg.metrics.completedDtbs.tooltip'),
      value: pctDisplay(data.completedDtbs.percent),
      badge: t('dashboard.riskPool.riskPoolOrg.metrics.completedDtbs.target'),
      status: data.completedDtbs.status,
    },
    {
      key: 'pendingPolicyUpdates',
      title: t('dashboard.riskPool.riskPoolOrg.metrics.pendingPolicyUpdates.title'),
      tooltip: t('dashboard.riskPool.riskPoolOrg.metrics.pendingPolicyUpdates.tooltip'),
      value: String(data.pendingPolicyUpdates.count),
      badge: t('dashboard.riskPool.riskPoolOrg.metrics.pendingPolicyUpdates.target'),
      status: data.pendingPolicyUpdates.status,
    },
    {
      key: 'policiesInIssuedStatus',
      title: t('dashboard.riskPool.riskPoolOrg.metrics.policiesInIssuedStatus.title'),
      tooltip: t('dashboard.riskPool.riskPoolOrg.metrics.policiesInIssuedStatus.tooltip'),
      value: pctDisplay(data.policiesInIssuedStatus.percent),
      badge: `${data.policiesInIssuedStatus.count} of ${data.policiesInIssuedStatus.totalCount}`,
      status: 'GOOD' as MetricStatus,
    },
    {
      key: 'dtbAssignmentsCreated',
      title: t('dashboard.riskPool.riskPoolOrg.metrics.dtbAssignmentsCreated.title'),
      tooltip: t('dashboard.riskPool.riskPoolOrg.metrics.dtbAssignmentsCreated.tooltip'),
      value: pctDisplay(data.dtbAssignmentsCreated.percent),
      badge: t('dashboard.riskPool.riskPoolOrg.metrics.dtbAssignmentsCreated.target'),
      status: data.dtbAssignmentsCreated.status,
    },
  ];
};

const CARD_GRID_SX = {
  display: 'grid',
  gridTemplateColumns: {
    xs: '1fr',
    sm: 'repeat(2, 1fr)',
    lg: 'repeat(3, 1fr)',
  },
  gap: 2,
} as const;

export function MetricsCards() {
  const { orgId, date, hasManualContent } = useRiskPoolOrgContext();
  const { data: metrics, isLoading } = usePolicyMetrics(orgId, { date });

  if (isLoading) {
    return (
      <Box sx={{ width: '100%', height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!metrics) return null;

  const cards = getCardsConfig(metrics);

  return (
    <Box sx={CARD_GRID_SX}>
      {cards.map(({ key, ...card }) => (
        <MetricCard key={key} {...card} noData={!hasManualContent} />
      ))}
    </Box>
  );
}

export default MetricsCards;
